import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import ConfirmModal from './ConfirmModal';

const TaskFiles = ({ taskId, isTaskEditor }) => {
  const { currentLang, token, API_URL, getCsrfToken, user } = useAuth();
  
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [editFileName, setEditFileName] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [deletingFile, setDeletingFile] = useState(null);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, [taskId]);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${taskId}/files`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setFiles(data.data || []);
      } else {
        throw new Error(data.message || 'Ошибка загрузки файлов');
      }
    } catch (error) {
      console.error('Ошибка загрузки файлов:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, []);

  const handleFiles = async (fileList) => {
    if (fileList.length === 0) return;

    setUploading(true);
    setUploadProgress({});

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const formData = new FormData();
      Array.from(fileList).forEach((file) => {
        formData.append('files[]', file);
      });

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(prev => ({ ...prev, overall: percentComplete }));
        }
      });

            xhr.addEventListener('load', () => {
        console.log('XHR Status:', xhr.status);
        console.log('XHR Response:', xhr.responseText);
        
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            if (response.success) {
              showNotification(
                currentLang === 'ru' ? 'Файлы загружены!' : 'Files uploaded!',
                'success'
              );
              fetchFiles();
            } else {
              throw new Error(response.message || 'Ошибка загрузки файлов');
            }
          } catch (error) {
            console.error('Ошибка парсинга ответа:', error);
            console.log('Ответ сервера:', xhr.responseText);
            
            // Проверяем, содержит ли ответ success: true
            if (xhr.responseText.includes('"success":true') || xhr.responseText.includes('"success": true')) {
              showNotification(
                currentLang === 'ru' ? 'Файлы загружены!' : 'Files uploaded!',
                'success'
              );
              fetchFiles();
            } else {
              showNotification(
                currentLang === 'ru' ? 'Ошибка обработки ответа сервера' : 'Server response processing error',
                'error'
              );
            }
          }
        } else {
          console.error('HTTP Error:', xhr.status, xhr.statusText);
          showNotification(
            currentLang === 'ru' ? `Ошибка загрузки файлов (${xhr.status})` : `File upload error (${xhr.status})`,
            'error'
          );
        }
        setUploading(false);
        setUploadProgress({});
      });

      xhr.addEventListener('error', () => {
        showNotification('Ошибка загрузки файлов', 'error');
        setUploading(false);
        setUploadProgress({});
      });

      xhr.open('POST', `${API_URL}${currentLang}/seo-tasks/${taskId}/files`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.setRequestHeader('X-CSRF-Token', currentCsrfToken);
      xhr.send(formData);

    } catch (error) {
      console.error('Ошибка загрузки файлов:', error);
      showNotification(error.message, 'error');
      setUploading(false);
      setUploadProgress({});
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleRename = async (fileId, newName) => {
    if (!newName.trim()) return;

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${taskId}/files/${fileId}/rename`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ new_name: newName.trim() }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setEditingFile(null);
        setEditFileName('');
        fetchFiles();
        showNotification(
          currentLang === 'ru' ? 'Файл переименован!' : 'File renamed!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка переименования файла');
      }
    } catch (error) {
      console.error('Ошибка переименования файла:', error);
      showNotification(error.message, 'error');
    }
  };

  const showDeleteConfirm = (fileId) => {
    setFileToDelete(fileId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;

    setDeletingFile(fileToDelete);

    try {
      const currentCsrfToken = await getCsrfToken();
      if (!currentCsrfToken) {
        throw new Error('Не удалось получить CSRF токен');
      }

      const response = await fetch(`${API_URL}${currentLang}/seo-tasks/${taskId}/files/${fileToDelete}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CSRF-Token': currentCsrfToken,
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        fetchFiles();
        showNotification(
          currentLang === 'ru' ? 'Файл удален!' : 'File deleted!',
          'success'
        );
      } else {
        throw new Error(data.message || 'Ошибка удаления файла');
      }
    } catch (error) {
      console.error('Ошибка удаления файла:', error);
      showNotification(error.message, 'error');
    } finally {
      setDeletingFile(null);
      setFileToDelete(null);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setFileToDelete(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const getFileIcon = (type) => {
    const iconMap = {
      pdf: '📄',
      doc: '📝',
      docx: '📝',
      xls: '📊',
      xlsx: '📊',
      ppt: '📋',
      pptx: '📋',
      txt: '📄',
      jpg: '🖼️',
      jpeg: '🖼️',
      png: '🖼️',
      gif: '🖼️',
      zip: '📦',
      rar: '📦',
      mp4: '🎥',
      mp3: '🎵',
      default: '📎'
    };
    return iconMap[type.toLowerCase()] || iconMap.default;
  };

  const showNotification = (message, type = 'success') => {
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500',
      warning: 'bg-yellow-500'
    };
    
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 ${colors[type]} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full`;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);
    
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const isOwnFile = (file) => {
    return user?.id === file.uid;
  };

  return (
    <div className="flex flex-col h-full">
      {/* Заголовок */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {currentLang === 'ru' ? 'Файлы задачи' : 'Task Files'}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          {currentLang === 'ru' 
            ? 'Загружайте и управляйте файлами, связанными с задачей' 
            : 'Upload and manage files related to the task'
          }
        </p>
      </div>

      {/* Ошибка */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Зона загрузки */}
      {isTaskEditor && (
      <div className="mb-6">
        <div
          ref={dropZoneRef}
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200 ${
            dragActive 
              ? 'border-brand-500 bg-brand-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDragIn}
          onDragLeave={handleDragOut}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="space-y-4">
            <div className="text-6xl">📁</div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                {currentLang === 'ru' ? 'Перетащите файлы сюда' : 'Drag files here'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {currentLang === 'ru' ? 'или' : 'or'}
              </p>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading 
                ? (currentLang === 'ru' ? 'Загрузка...' : 'Uploading...') 
                : (currentLang === 'ru' ? 'Выбрать файлы' : 'Select Files')
              }
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInput}
              className="hidden"
              disabled={uploading}
            />
          </div>
        </div>

        {/* Прогресс загрузки */}
        {uploading && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">
                {currentLang === 'ru' ? 'Загрузка файлов...' : 'Uploading files...'}
              </span>
              <span className="text-sm text-blue-700">
                {uploadProgress.overall || 0}%
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${uploadProgress.overall || 0}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Список файлов */}
      <div className="flex-1">
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
          </div>
        ) : files.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-6xl mb-4">📂</div>
            <p className="text-lg font-medium text-gray-900 mb-2">
              {currentLang === 'ru' ? 'Нет файлов' : 'No files'}
            </p>
            <p className="text-sm">
              {currentLang === 'ru' 
                ? 'Загрузите первый файл для начала работы' 
                : 'Upload your first file to get started'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {files.map((file) => (
              <div 
                key={file.id} 
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                {/* Иконка и название */}
                <div className="flex items-start space-x-3 mb-3">
                  <div className="text-3xl flex-shrink-0">
                    {getFileIcon(file.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    {editingFile === file.id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editFileName}
                          onChange={(e) => setEditFileName(e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
                          autoFocus
                        />
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleRename(file.id, editFileName)}
                            className="px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                          >
                            {currentLang === 'ru' ? 'Сохранить' : 'Save'}
                          </button>
                          <button
                            onClick={() => {
                              setEditingFile(null);
                              setEditFileName('');
                            }}
                            className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                          >
                            {currentLang === 'ru' ? 'Отмена' : 'Cancel'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="group">
                        <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Информация о файле */}
                <div className="space-y-2 text-xs text-gray-500">
                  <div className="flex justify-between">
                    <span>{currentLang === 'ru' ? 'Загружен:' : 'Uploaded:'}</span>
                    <span>{formatDate(file.date)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{currentLang === 'ru' ? 'Автор:' : 'Author:'}</span>
                    <span className="truncate ml-2">{file.user_name || `ID: ${file.uid}`}</span>
                  </div>
                </div>

                {/* Действия */}
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                  <a
                    href={`${API_URL}${file.url.substring(1)}`}
                    download={file.name}
                    className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    {currentLang === 'ru' ? 'Скачать' : 'Download'}
                  </a>
                  
                  {isOwnFile(file) && (
                    <div className="flex space-x-1">
                      <button
                        onClick={() => {
                          setEditingFile(file.id);
                          setEditFileName(file.name);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100"
                        title={currentLang === 'ru' ? 'Переименовать' : 'Rename'}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => showDeleteConfirm(file.id)}
                        disabled={deletingFile === file.id}
                        className="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-50"
                        title={currentLang === 'ru' ? 'Удалить' : 'Delete'}
                      >
                        {deletingFile === file.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Модалка подтверждения удаления */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title={currentLang === 'ru' ? 'Подтверждение удаления' : 'Confirm Deletion'}
        message={currentLang === 'ru' 
          ? 'Вы уверены, что хотите удалить этот файл? Это действие нельзя отменить.' 
          : 'Are you sure you want to delete this file? This action cannot be undone.'
        }
        confirmText={currentLang === 'ru' ? 'Удалить' : 'Delete'}
        cancelText={currentLang === 'ru' ? 'Отмена' : 'Cancel'}
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        currentLang={currentLang}
      />
    </div>
  );
};

export default TaskFiles; 